import { patch, readonly, writable, type ReadableStore } from '../store.js'
import { cameraCaptureFor } from '@live-stream/media'
import { COMMUNICATIONS_DEVICE_ID } from './devices.js'

/**
 * Local camera and microphone acquisition.
 *
 * Every getUserMedia failure gets its own message and its own recovery path.
 * Collapsing them into "could not access camera" is the single largest source
 * of "the app doesn't work" reports, because the five causes need five
 * different actions from the user — and two of them are not about permission
 * at all.
 */

export type MediaErrorKind =
  | 'denied'
  | 'no-device'
  | 'in-use'
  | 'over-constrained'
  | 'insecure-context'
  | 'unsupported'
  | 'unknown'

export interface MediaFailure {
  kind: MediaErrorKind
  /** Shown to the user. Says what happened and what to do about it. */
  message: string
  /** True when retrying with relaxed constraints might work. */
  retryable: boolean
}

export function classifyMediaError(err: unknown): MediaFailure {
  const name = (err as { name?: string })?.name ?? ''

  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return {
        kind: 'denied',
        message:
          'Camera and microphone access was blocked. Click the camera icon in your browser’s address bar to allow it, then try again.',
        retryable: false,
      }
    case 'NotFoundError':
    case 'OverconstrainedError' /* handled below, but a missing deviceId lands here too */:
      if (name === 'OverconstrainedError') {
        return {
          kind: 'over-constrained',
          message: 'That device cannot do the requested quality. Retrying with relaxed settings.',
          retryable: true,
        }
      }
      return {
        kind: 'no-device',
        message: 'No camera or microphone was found. Connect one and try again.',
        retryable: true,
      }
    case 'NotReadableError':
    case 'AbortError':
      // Very common on Windows: Teams, Zoom or another tab holds the device.
      return {
        kind: 'in-use',
        message:
          'Your camera or microphone is being used by another app. Close the other app or tab, then try again.',
        retryable: true,
      }
    case 'TypeError':
      return {
        kind: 'unsupported',
        message: 'This browser cannot capture media. Try Chrome, Edge, Firefox or Safari.',
        retryable: false,
      }
    default:
      return {
        kind: 'unknown',
        message: `Could not start your camera or microphone (${name || 'unknown error'}).`,
        retryable: true,
      }
  }
}

export interface MediaConstraintPrefs {
  audioDeviceId?: string
  videoDeviceId?: string
  width?: number
  height?: number
  frameRate?: number
}

/** The size to open the camera at on this machine (sfu-policy.ts cameraCaptureFor). */
function cameraCaptureHere(): { width: number; height: number } {
  if (typeof navigator === 'undefined') return { width: 1280, height: 720 }
  const mobile =
    (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile ??
    /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)
  return cameraCaptureFor({ cores: navigator.hardwareConcurrency || 4, mobile })
}

function buildConstraints(prefs: MediaConstraintPrefs, relaxed: boolean): MediaStreamConstraints {
  const capture = cameraCaptureHere()
  return {
    audio: {
      // A chosen device is asked for exactly. With no choice we ask, as an
      // ideal, for the OS's communications device — see COMMUNICATIONS_DEVICE_ID
      // for why that is what makes a freshly connected headset take over on
      // Windows. Everywhere else the id does not exist and `ideal` is ignored.
      deviceId: prefs.audioDeviceId
        ? { exact: prefs.audioDeviceId }
        : { ideal: COMMUNICATIONS_DEVICE_ID },
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      // Mono: speech gains nothing from stereo and it doubles the bitrate.
      channelCount: 1,
    },
    video: relaxed
      ? true
      : {
          ...(prefs.videoDeviceId ? { deviceId: { exact: prefs.videoDeviceId } } : {}),
          width: { ideal: prefs.width ?? capture.width },
          height: { ideal: prefs.height ?? capture.height },
          frameRate: { ideal: prefs.frameRate ?? 30, max: 30 },
          facingMode: 'user',
        },
  }
}

export interface LocalMediaState {
  stream: MediaStream | null
  audioTrack: MediaStreamTrack | null
  videoTrack: MediaStreamTrack | null
  failure: MediaFailure | null
  acquiring: boolean
  /** True while the camera is being re-acquired after being turned off. */
  resuming: boolean
  /**
   * True while the camera source has stopped delivering frames although the
   * track is live: a phone that backgrounded the tab or locked its screen, a
   * laptop whose camera another app took. The browser says so with the
   * track's mute event. Everyone else would otherwise stare at a frozen
   * frame under a "camera on" label. Debounced, because some drivers mute
   * for a moment when the camera starts or its constraints change.
   */
  videoPaused: boolean
  /**
   * The camera to open next: chosen in the picker, or remembered from the
   * track we stopped when the camera was turned off. Asking by id skips the
   * browser's device selection and guarantees the same camera comes back —
   * `facingMode: 'user'` alone can pick a different front camera on phones
   * that have two.
   */
  preferredVideoDeviceId: string | null
}

export type LostKind = 'audio' | 'video'

export class LocalMedia {
  private readonly store = writable<LocalMediaState>({
    stream: null,
    audioTrack: null,
    videoTrack: null,
    failure: null,
    acquiring: false,
    resuming: false,
    videoPaused: false,
    preferredVideoDeviceId: null,
  })
  readonly state: ReadableStore<LocalMediaState> = readonly(this.store)
  private lostHandler: ((kind: LostKind) => void) | null = null
  private pauseTimer: ReturnType<typeof setTimeout> | null = null

  private get s(): LocalMediaState {
    return this.store.get()
  }

  private watchPause(track: MediaStreamTrack): void {
    patch(this.store, { videoPaused: false })
    track.addEventListener('mute', () => {
      if (this.pauseTimer) clearTimeout(this.pauseTimer)
      this.pauseTimer = setTimeout(() => {
        if (this.s.videoTrack === track && track.muted) patch(this.store, { videoPaused: true })
      }, 1500)
    })
    track.addEventListener('unmute', () => {
      if (this.pauseTimer) clearTimeout(this.pauseTimer)
      this.pauseTimer = null
      if (this.s.videoTrack === track) patch(this.store, { videoPaused: false })
    })
  }

  /** Called when the device behind a live track disappears. One handler; the media controller owns it. */
  onTrackLost(handler: (kind: LostKind) => void): void {
    this.lostHandler = handler
  }

  /**
   * `ended` fires when the SOURCE goes away — unplugged, revoked, grabbed by
   * the OS — and never for our own stop(). So it means exactly "the device
   * vanished under us", and the call must not go with it.
   */
  private watchLoss(track: MediaStreamTrack, kind: LostKind): void {
    if (kind === 'video') this.watchPause(track)
    track.addEventListener('ended', () => {
      const current = kind === 'audio' ? this.s.audioTrack : this.s.videoTrack
      if (current !== track) return // already swapped out; nothing to recover
      this.s.stream?.removeTrack(track)
      if (kind === 'audio') patch(this.store, { audioTrack: null })
      else patch(this.store, { preferredVideoDeviceId: null, videoTrack: null })
      this.lostHandler?.(kind)
    })
  }

  /**
   * Counts the stops. A camera or microphone that finishes opening after a stop (the permission
   * prompt answered late, a page already on its way elsewhere) belongs to a call that is over, and
   * is closed on arrival: otherwise its light stays on with nobody holding it.
   */
  private epoch = 0
  private discardIfStopped(stream: MediaStream, epoch: number): boolean {
    if (epoch === this.epoch) return false
    stream.getTracks().forEach((t) => t.stop())
    return true
  }

  private adopt(next: MediaStream): void {
    const audioTrack = next.getAudioTracks()[0] ?? null
    const videoTrack = next.getVideoTracks()[0] ?? null
    patch(this.store, { stream: next, audioTrack, videoTrack, failure: null })
    if (audioTrack) this.watchLoss(audioTrack, 'audio')
    if (videoTrack) this.watchLoss(videoTrack, 'video')
  }

  /** A track added to the stream we hold, or to a new one if none: the stream object stays stable. */
  private addToStream(track: MediaStreamTrack): MediaStream {
    const stream = this.s.stream
    if (stream) {
      stream.addTrack(track)
      return stream
    }
    return new MediaStream([track])
  }

  async start(prefs: MediaConstraintPrefs = {}): Promise<boolean> {
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      patch(this.store, {
        failure: {
          kind: 'insecure-context',
          message:
            'Camera access requires a secure connection (https). Open the app over https and try again.',
          retryable: false,
        },
      })
      return false
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      patch(this.store, { failure: classifyMediaError({ name: 'TypeError' }) })
      return false
    }

    patch(this.store, { acquiring: true })
    const epoch = this.epoch
    try {
      const got = await navigator.mediaDevices.getUserMedia(buildConstraints(prefs, false))
      if (this.discardIfStopped(got, epoch)) return false
      this.adopt(got)
      return true
    } catch (err) {
      const first = classifyMediaError(err)

      // One relaxed retry. An exact deviceId that has been unplugged, or a
      // resolution the camera cannot do, both fail here and both succeed on
      // the second attempt — so retrying silently beats showing an error.
      if (first.retryable) {
        try {
          const got = await navigator.mediaDevices.getUserMedia(buildConstraints({}, true))
          if (this.discardIfStopped(got, epoch)) return false
          this.adopt(got)
          return true
        } catch (retryErr) {
          patch(this.store, { failure: classifyMediaError(retryErr) })
          return false
        }
      }

      patch(this.store, { failure: first })
      return false
    } finally {
      patch(this.store, { acquiring: false })
    }
  }

  /**
   * Turning the camera off means STOPPING the track, not disabling it.
   *
   * `track.enabled = false` keeps the hardware LED on, and users read a lit
   * camera light as the app spying on them. It also keeps sending black
   * frames. Stopping the track is the only thing that extinguishes the LED.
   */
  stopVideo(): void {
    const track = this.s.videoTrack
    if (!track) return
    const preferredVideoDeviceId = track.getSettings().deviceId ?? this.s.preferredVideoDeviceId
    track.stop()
    this.s.stream?.removeTrack(track)
    patch(this.store, { videoPaused: false, preferredVideoDeviceId, videoTrack: null })
  }

  /**
   * Close the microphone: a big meeting's attendee who may not speak has no
   * reason to hold it open, and the browser's mic light should say so.
   * switchAudio opens it again.
   */
  stopAudio(): void {
    const track = this.s.audioTrack
    if (!track) return
    track.stop()
    this.s.stream?.removeTrack(track)
    patch(this.store, { audioTrack: null })
  }

  /**
   * Re-acquire video after stopVideo.
   *
   * The wait here is the camera hardware opening — around 300ms on a good
   * webcam, over a second on many phones — and nothing in software shortens
   * it. It is the price of the LED going out. What software CAN do is make
   * sure nothing else is in front of it: the preview switches the instant the
   * track exists, and the server round trip decides only when others see it.
   */
  async resumeVideo(prefs: MediaConstraintPrefs = {}): Promise<MediaStreamTrack | null> {
    patch(this.store, { resuming: true })
    const epoch = this.epoch

    const deviceId = prefs.videoDeviceId ?? this.s.preferredVideoDeviceId
    const wanted: MediaConstraintPrefs = deviceId ? { ...prefs, videoDeviceId: deviceId } : prefs

    const open = async (relaxed: boolean): Promise<MediaStreamTrack | null> => {
      const fresh = await navigator.mediaDevices.getUserMedia({
        video: buildConstraints(relaxed ? {} : wanted, relaxed).video,
      })
      if (this.discardIfStopped(fresh, epoch)) return null
      return fresh.getVideoTracks()[0] ?? null
    }

    try {
      let track: MediaStreamTrack | null
      try {
        track = await open(false)
      } catch (err) {
        // Same single relaxed retry as start(): the camera we remembered may
        // have been unplugged, or another app may now hold it.
        if (!classifyMediaError(err).retryable) throw err
        track = await open(true)
      }
      if (track) {
        patch(this.store, { stream: this.addToStream(track), videoTrack: track })
        this.watchLoss(track, 'video')
      }
      return track
    } catch (err) {
      patch(this.store, { failure: classifyMediaError(err) })
      return null
    } finally {
      patch(this.store, { resuming: false })
    }
  }

  /**
   * Swap microphones without touching the peer connection: a new track from
   * the chosen device, which the caller then hands to the sender with
   * replaceTrack. Muted stays muted — the new track inherits `enabled`.
   * null means "no preference": the platform's call device, or the default.
   */
  async switchAudio(deviceId: string | null): Promise<MediaStreamTrack | null> {
    const epoch = this.epoch
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        audio: buildConstraints(deviceId ? { audioDeviceId: deviceId } : {}, false).audio,
      })
      if (this.discardIfStopped(fresh, epoch)) return null
      const track = fresh.getAudioTracks()[0]
      if (!track) return null

      const old = this.s.audioTrack
      track.enabled = old?.enabled ?? true
      if (old) {
        old.stop()
        this.s.stream?.removeTrack(old)
      }
      patch(this.store, { stream: this.addToStream(track), audioTrack: track, failure: null })
      this.watchLoss(track, 'audio')
      return track
    } catch (err) {
      patch(this.store, { failure: classifyMediaError(err) })
      return null
    }
  }

  /**
   * Choose a camera. If the camera is on, swap it live; if it is off, the
   * choice waits for the next resume — so the picker works either way.
   */
  async switchVideo(deviceId: string | null): Promise<boolean> {
    patch(this.store, { preferredVideoDeviceId: deviceId })
    const old = this.s.videoTrack
    if (!old) return true
    const epoch = this.epoch

    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        video: buildConstraints(deviceId ? { videoDeviceId: deviceId } : {}, false).video,
      })
      if (this.discardIfStopped(fresh, epoch)) return false
      const track = fresh.getVideoTracks()[0]
      if (!track) return false

      old.stop()
      this.s.stream?.removeTrack(old)
      patch(this.store, { stream: this.addToStream(track), videoTrack: track, failure: null })
      this.watchLoss(track, 'video')
      return true
    } catch (err) {
      patch(this.store, { failure: classifyMediaError(err) })
      return false
    }
  }

  /** Mute is the cheap direction: keep the track, send silence, resume instantly. */
  setAudioEnabled(enabled: boolean): void {
    const track = this.s.audioTrack
    if (track) track.enabled = enabled
  }

  stop(): void {
    this.epoch++
    this.s.stream?.getTracks().forEach((t) => t.stop())
    patch(this.store, { videoPaused: false, stream: null, audioTrack: null, videoTrack: null })
  }
}
